const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
const MEGAMENU_CLOSE_DELAY = 150;

document.addEventListener('DOMContentLoaded', () => {
  initMobileDrawer();
  initGenderTabs();
  initDesktopMegaMenu();
  initKwikpassAccountDrawer();
  initHeaderScroll();
  initHeaderMetrics();
});

// Shopify puts .shopify-section-group-header-group on every section in the group — the
// announcement bar, the header and both drawers each carry it, and there is no element
// wrapping them. Selecting by that class returns whichever section comes first, which is
// the announcement bar as soon as one is enabled. Address each by its own schema class.
const ANNOUNCEMENT_SECTION = '.announcement-bar-section';
const HEADER_SECTION = '.header-section';

// Publishes the header's height so CSS can sit things directly beneath it — currently the
// PLP filter bar, which pins at --header-group-h rather than at 0. Measured rather than
// hardcoded because it is not a constant: 84px on desktop, 69px on mobile. The announcement
// bar is not part of it; the bar scrolls away, so by the time anything is pinned beneath the
// header the bar is gone.
function initHeaderMetrics() {
  const headerSection = document.querySelector(HEADER_SECTION);
  if (!headerSection) return;

  const syncHeaderHeight = () => {
    document.documentElement.style.setProperty(
      '--header-group-h',
      `${headerSection.offsetHeight}px`
    );
  };

  syncHeaderHeight();
  if ('ResizeObserver' in window) {
    new ResizeObserver(syncHeaderHeight).observe(headerSection);
  }
}

function initMobileDrawer() {
  const trigger = document.getElementById('mobile-menu-trigger');
  const drawer = document.getElementById('mobile-menu-drawer');

  if (!trigger || !drawer) return;

  const panel = drawer.querySelector('.mobile-menu-panel');
  const backdrop = drawer.querySelector('.mobile-menu-backdrop');
  const header = document.getElementById('mobile-header');
  let isOpen = false;

  const menuPanels = [...drawer.querySelectorAll('[data-panel]')];

  const resetMenu = () => {
    menuPanels.forEach((p) => {
      p.classList.toggle('is-active', p.dataset.panel === 'root');
      p.classList.remove('is-left');
    });
  };

  const activatePanel = (id, direction) => {
    const current = menuPanels.find((p) => p.classList.contains('is-active'));
    const target = menuPanels.find((p) => p.dataset.panel === id);
    if (!target || current === target) return;

    if (current) {
      current.classList.remove('is-active');
      // Forward: parent parks to the left. Back: leaving panel exits to the right.
      current.classList.toggle('is-left', direction === 'forward');
    }
    target.classList.remove('is-left');
    target.classList.add('is-active');
  };

  const openMenu = () => {
    isOpen = true;
    drawer.classList.remove('hidden');
    drawer.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    header?.classList.add('menu-open');
    resetMenu();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        backdrop.classList.remove('opacity-0');
        backdrop.classList.add('opacity-100');
        panel.classList.remove('translate-x-full');
        panel.classList.add('translate-x-0');
      });
    });

    // preventScroll: the panel is still parked off-viewport (translate-x-full)
    // at this point, so a plain focus() scrolls the page behind the drawer.
    const firstFocusable = panel.querySelector(FOCUSABLE_SELECTOR);
    firstFocusable?.focus({ preventScroll: true });
  };

  const closeMenu = () => {
    isOpen = false;
    trigger.setAttribute('aria-expanded', 'false');
    header?.classList.remove('menu-open');

    backdrop.classList.remove('opacity-100');
    backdrop.classList.add('opacity-0');
    panel.classList.remove('translate-x-0');
    panel.classList.add('translate-x-full');

    setTimeout(() => {
      drawer.classList.add('hidden');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
    }, 800); // keep in sync with --drawer-duration in base.css

    trigger.focus({ preventScroll: true });
  };

  // Keep Tab focus inside the open drawer.
  const trapFocus = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = [...panel.querySelectorAll(FOCUSABLE_SELECTOR)].filter(
      (el) => el.offsetParent !== null
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  trigger.addEventListener('click', () => (isOpen ? closeMenu() : openMenu()));

  drawer.addEventListener('click', (event) => {
    if (event.target.closest('[data-mobile-menu-close]')) {
      closeMenu();
      return;
    }

    // Opening search from the menu: close the drawer (search-modal.js opens the modal).
    if (event.target.closest('.search-trigger')) {
      closeMenu();
      return;
    }

    const opener = event.target.closest('[data-menu-open]');
    if (opener) {
      activatePanel(opener.dataset.menuOpen, 'forward');
      return;
    }

    const back = event.target.closest('[data-menu-back]');
    if (back) {
      const active = menuPanels.find((p) => p.classList.contains('is-active'));
      activatePanel(active?.dataset.parent || 'root', 'back');
    }
  });

  drawer.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isOpen) closeMenu();
    if (isOpen) trapFocus(event);
  });
}

function initGenderTabs() {
  const tabs = [...document.querySelectorAll('[data-gender-tab]')];
  const panels = [...document.querySelectorAll('[data-gender-panel]')];

  if (tabs.length === 0) return;

  const activateGender = (index) => {
    for (const tab of tabs) {
      const isActive = tab.dataset.genderTab === index;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
    for (const panel of panels) {
      panel.classList.toggle('hidden', panel.dataset.genderPanel !== index);
    }
  };

  for (const tab of tabs) {
    tab.addEventListener('click', () => activateGender(tab.dataset.genderTab));
  }
}

function initDesktopMegaMenu() {
  const navItems = [...document.querySelectorAll('[data-nav-item]')];
  const closers = [];

  for (const navItem of navItems) {
    const trigger = navItem.querySelector('[data-nav-trigger]');
    const megaMenu = navItem.querySelector('[data-megamenu]');
    if (!megaMenu) continue;

    let closeTimer;

    const open = () => {
      clearTimeout(closeTimer);
      navItem.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
    };

    const close = () => {
      navItem.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
    };

    navItem.addEventListener('mouseenter', open);
    navItem.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(close, MEGAMENU_CLOSE_DELAY);
    });
    navItem.addEventListener('focusin', open);
    navItem.addEventListener('focusout', (event) => {
      if (!navItem.contains(event.relatedTarget)) close();
    });
    navItem.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        trigger?.focus();
      }
    });

    closers.push(close);
  }

  if (closers.length === 0) return;

  // The mega menu is absolutely positioned inside the header, so it rides the page:
  // scrolling with it open drags the panel along with the content behind it. Locking
  // <body> instead would pin the page whenever the cursor merely rests on the nav,
  // and dropping the scrollbar shifts the layout sideways — so close on scroll intent.
  // `wheel`/`touchmove` land before `scroll`, so initHeaderScroll never sees a
  // half-open state; the `scroll` listener catches scrollbar drags and keyboard keys.
  const closeOnScrollIntent = () => {
    if (!document.querySelector('[data-nav-item].is-open')) return;
    for (const close of closers) close();
  };

  for (const type of ['wheel', 'touchmove', 'scroll']) {
    window.addEventListener(type, closeOnScrollIntent, { passive: true });
  }
}

// The KwikPass account dropdown is injected into the header by the app embed, not rendered
// by the theme, so none of the theme's own drawer plumbing reaches it and the markup cannot
// be edited. The embed opens it and then leaves it open — its only dismiss control is its own
// X — so scrolling with it open leaves the panel hanging over the page while the header slides
// out from under it (initHeaderScroll hides the header on scroll down), and clicking anywhere
// else never closes it either. Give it the dismiss behaviour every other panel here has.
//
// Nothing is queried at bind time and the listeners sit on window/document, because the embed
// injects its markup well after DOMContentLoaded; the handlers look the elements up when they
// run, so a late injection needs no observer.
function initKwikpassAccountDrawer() {
  // Prefix match rather than the exact id: the embed renders '-desktop' and '-mobile' variants
  // of the same panel depending on viewport, and both want the same treatment.
  const DRAWER = '[id^="kwikpass-drawer"]';
  const CLOSE = '#icon-close-kwikpass';
  // The open/close icons are siblings of the panel, not children, so an outside click has to
  // count them as inside — otherwise the click that opens the panel, which reaches document
  // after the embed's own handler has run, would immediately close it again. Both the icon
  // block and the wrapper the embed mounts the whole thing into are listed: either one alone
  // would do today, and naming both means a markup change on the embed's side degrades to
  // "outside click stops working" rather than "the panel can no longer be opened".
  const ICONS = '#loggedin-icons, .header--icon';

  const isOpen = () =>
    [...document.querySelectorAll(DRAWER)].some(
      (drawer) => getComputedStyle(drawer).display !== 'none'
    );

  // Closing goes through the embed's own X instead of hiding the panel here: it carries
  // whatever handler the embed attached, so the icon swap behind it stays in step and the
  // theme never touches the inline styles the embed manages.
  const close = () => {
    if (!isOpen()) return;
    document.querySelector(CLOSE)?.click();
  };

  // `wheel`/`touchmove` land before `scroll`, so the panel is gone before the header starts
  // translating; the `scroll` listener catches scrollbar drags and keyboard scrolling. Read
  // the panel's visibility in a frame rather than inline — getComputedStyle forces a style
  // recalc, and these fire once per frame while a scroll is in flight.
  let pending = false;
  const closeOnScrollIntent = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      close();
    });
  };

  for (const type of ['wheel', 'touchmove', 'scroll']) {
    window.addEventListener(type, closeOnScrollIntent, { passive: true });
  }

  document.addEventListener('click', (event) => {
    if (event.target?.closest?.(`${DRAWER}, ${ICONS}`)) return;
    close();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') close();
  });
}

function initHeaderScroll() {
  const headerSection = document.querySelector(HEADER_SECTION);
  const announcement = document.querySelector(ANNOUNCEMENT_SECTION);
  const mainHeader = document.getElementById('main-header');
  if (!headerSection) return;

  // The product page lifts the header out of flow so the gallery can run under it, which
  // also loses the offset sticky would have applied while the announcement bar is still on
  // screen. Feed that offset back: the bar's remaining visible height, shrinking to 0 as it
  // scrolls off, so the header rides down with it instead of jumping. Set on the header
  // rather than :root so the write does not invalidate the whole document every frame.
  let announcementHeight = announcement?.offsetHeight ?? 0;
  if (announcement && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      announcementHeight = announcement.offsetHeight;
    }).observe(announcement);
  }

  const syncHeaderOffset = (scrollY) => {
    if (!announcement) return;
    const remaining = Math.max(0, announcementHeight - scrollY);
    headerSection.style.setProperty('--header-offset', `${remaining}px`);
  };

  syncHeaderOffset(window.scrollY);

  const SCROLLED_THRESHOLD = 10;
  const HIDE_THRESHOLD = 80;
  let lastScrollY = window.scrollY;
  let ticking = false;

  const onScroll = () => {
    // Freeze the header while a drawer or mega menu is open: locking the page
    // can shift the scroll offset under us (iOS pins <body> at the top), and a
    // header sliding in or out behind an overlay reads as a glitch.
    const menuOpen =
      document.body.classList.contains('overflow-hidden') ||
      document.querySelector('[data-nav-item].is-open') !== null;

    if (menuOpen) {
      ticking = false;
      return;
    }

    const currentY = window.scrollY;
    const goingDown = currentY > lastScrollY;
    const isScrolled = currentY > SCROLLED_THRESHOLD;

    syncHeaderOffset(currentY);

    headerSection.classList.toggle('is-scrolled', isScrolled);
    mainHeader?.classList.toggle('shadow-sm', isScrolled);

    // Never start hiding before the bar has fully scrolled off, or the header would
    // translate by its own height from a non-zero offset and leave a strip on screen.
    const hideThreshold = Math.max(HIDE_THRESHOLD, announcementHeight);

    if (goingDown && currentY > hideThreshold) {
      headerSection.classList.add('header-hidden');
    } else if (!goingDown) {
      headerSection.classList.remove('header-hidden');
    }

    lastScrollY = currentY;
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      window.requestAnimationFrame(onScroll);
      ticking = true;
    },
    { passive: true }
  );
}
